import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import './earning.css';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import { getTotalEarningsYearly, exportEarningsPdf } from '../../../../shared/actions/transactionAction';
import { showError, showLoading, closeAlert } from '../../../../shared/utils/swalHandler';
import { BREAKDOWN_COLORS, BREAKDOWN_FIELD_MAP } from '../../../../shared/constants/EarningsPayout';
import { formatUsdAmountDisplayCents } from '../../../../shared/utils/function';
import {
  shouldHideColumn,
  formatColumnName,
  formatCellValue,
  shouldHaveZeroClass,
  getSortedColumns,
  generateHtmlForPdf
} from '../../../../shared/utils/earningsPayoutUtil';
import { getVatRateByCountryCode } from '../../../../shared/constants/vatRates';
import { getGstRateByCountryCode } from '../../../../shared/constants/gstCountry';


const EarningsReport = ({
  // coming from mapStateToProps
  user,
  creator
}) => {
  const { year } = useParams();
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);

  const countryCode = user?.country;
  const isVatApplicable = useMemo(() => {
    return getVatRateByCountryCode.find(
      (vatRate) => vatRate.code === countryCode
    );
  }, [countryCode]);

  // const isGstApplicable = useMemo(() => {
  //   return getGstRateByCountryCode.find(
  //     (gstRate) => gstRate.code === countryCode
  //   );
  // }, [countryCode]);
  const isGstApplicable = false;

  const vatNumber = creator?.user_data?.vat_number || 'N/A';
  const gstNumber = creator?.user_data?.gst_number || 'N/A';
  const taxType = isVatApplicable ? 'vat' : isGstApplicable ? 'gst' : 'none';
  const taxNumber = isVatApplicable ? vatNumber : isGstApplicable ? gstNumber : 'N/A';

  const getEarningsReport = useCallback(async (year) => {
    try {
      setLoading(true);
      const result = await getTotalEarningsYearly(year);
      if (result.success && result.data && result.data.length > 0) {
        setEarningsData(result.data[0]);
      } else {
        showError('Failed to fetch report', result.error || 'No data available for the selected year.');
        setEarningsData(null);
      }
    } catch (error) {
      showError('Error fetching report', error.message || 'An unexpected error occurred while fetching the report.');
      setEarningsData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getEarningsReport(year);
  }, [year, getEarningsReport]);

  const handleDownload = async () => {
    if (!earningsData) {
      showError('No data available', 'Cannot download PDF. No earnings data available.');
      return;
    }

    // Show loading popup
    showLoading('Generating PDF...');

    try {
      const html = generateHtmlForPdf(earningsData, year, user, taxType, taxNumber);
      const result = await exportEarningsPdf(html);

      // Close loading popup
      closeAlert();

      if (!result.success) {
        showError('Download Failed', result.error || 'Failed to download PDF.');
      }
    } catch (error) {
      // Close loading popup
      closeAlert();
      showError('Error', error.message || 'An unexpected error occurred while downloading PDF.');
    }
  };

  const renderDailyEarningsTable = (dailyEarnings) => {
    if (dailyEarnings.length === 0) {
      return (
        <table className="earnings-table">
          <tbody>
            <tr>
              <td colSpan="100%" style={{ textAlign: 'center', padding: '1rem' }}>
                No daily earnings data available
              </td>
            </tr>
          </tbody>
        </table>
      );
    }

    const sortedKeys = getSortedColumns(dailyEarnings, user?.rol);

    return (
      <table className="earnings-table">
        <thead>
          <tr>
            {sortedKeys.map((key) => (
              <th key={key}>{formatColumnName(key).toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dailyEarnings.map((day, dayIndex) => (
            <tr key={dayIndex}>
              {sortedKeys.map((key) => {
                const value = day[key];
                const cellValue = formatCellValue(key, value, day);
                const hasZeroClass = shouldHaveZeroClass(key, value);
                return (
                  <td key={key} className={hasZeroClass ? 'zero-value' : ''}>
                    {cellValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="earnings-report-page">
      <ModernHeader
        customTitle="Earnings Report"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
        showIconInRight="fa-solid fa-download"
        onShowIconInRightClick={handleDownload}
      />

      {loading ? (
        <div className="earnings-report-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : earningsData ? (
        <div className="earnings-report-content">
          <div className="earnings-report-card summary-card mb-4">
            <div className="card-header">
              <div className="card-icon summary-icon">
                <i className="fa-solid fa-chart-line"></i>
              </div>
              <h3 className="card-title">Summary</h3>
            </div>
            <div className="card-body">
              <div className="summary-info d-flex justify-content-between">
                <div className="info-item w-100">
                  <p className="info-label">PERIOD</p>
                  <p className="info-value text-nowrap">{earningsData.Summary || 'N/A'}</p>
                </div>
                <div className="info-item w-100">
                  <p className="info-label">TOTAL EARNINGS</p>
                  <p className="info-value earnings-amount">${formatUsdAmountDisplayCents(earningsData.totalEarning)}</p>
                </div>
              </div>
            </div>
          </div>

          {(earningsData.motnthly || []).map((month, monthIndex) => {
            const {
              monthName,
              year: monthYear,
              vatRemitted,
              netEarnings,
              'daily-earnings': dailyEarnings = []
            } = month;
            const monthDisplay = `${monthName} ${monthYear}`;

            return (
              <div key={monthIndex} className={`monthly-card ${monthIndex > 0 ? 'mt-4' : ''}`}>
                <div className="monthly_card_header">
                  <div className="card-icon monthly-icon">
                    <i className="fa-solid fa-calendar"></i>
                  </div>
                  <div>
                    <h3 className="card-title">{monthDisplay}</h3>
                    {isVatApplicable && (
                      <>
                        <p className="vat-label">VAT Remitted</p>
                        <p className="vat-ref">{vatNumber}</p>
                      </>
                    )}
                  </div>
                  <div className="monthly-right">
                    <div className="amount-badge earnings-badge">
                      ${formatUsdAmountDisplayCents(netEarnings)}
                    </div>
                    {isVatApplicable && (
                      <div className="amount-badge vat-badge">
                        ${formatUsdAmountDisplayCents(vatRemitted || 0)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="monthly_card_body">
                  {renderDailyEarningsTable(dailyEarnings)}
                </div>
              </div>
            );
          })}

          <div className="earnings-report-card summary-card mb-5">
            <div className="card-header">
              <div className="card-icon summary-overview-icon">
                <i className="fa-solid fa-calculator"></i>
              </div>
              <h3 className="card-title">Summary Overview</h3>
            </div>
            <div className="card-body">
              {Object.entries(earningsData.overview || {})
                .filter(([key]) => !shouldHideColumn(key, user?.rol))
                .map(([key, value]) => {
                  const label = BREAKDOWN_FIELD_MAP[key] || key;
                  const color = BREAKDOWN_COLORS[label] || "#4C6FFF";
                  return (
                    <div key={key} className='revenue_item'>
                      <span className='revenue_color' style={{ background: color }}></span>
                      <span className='revenue_label'>{label}</span>
                      <span className='revenue_amount'>${formatUsdAmountDisplayCents(value)}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      ) : (
        <div className="earnings-report-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No data available for the selected year.</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
  creator: state.creator?.creator,
});

export default connect(mapStateToProps)(EarningsReport);